/* =========================================================
   AF — Cart Drawer: swipe-to-close + back button (phones), and the
   Sep 2026 redesign glue (last block). Markup: snippets/cart-drawer.liquid
   File: assets/af-cart-drawer.js

   Back button (below 990px):
   - Open drawer => push a history entry (no URL change)
   - Back gesture => entry popped => close drawer
   - Close actions (swipe / close buttons) => remove our entry without
     navigating away

   Swipe down to close (only while the drawer is a bottom sheet, < 750px):
   - from the grab handle / header always
   - from the list only when it's scrolled to the top + a downward swipe
   - never hijacks normal scrolling; capture listeners survive re-renders
   ========================================================= */
(() => {
  try {
    if (window.__af_cart_drag_safe) return;
    window.__af_cart_drag_safe = true;

    /* =========================================================
       Mobile history (gesture-safe) — NO URL CHANGE
       pushState without URL prevents GA4/Meta false page_view events.
       Back gesture detected via popstate (not hashchange).
       ========================================================= */
    const mqDesktop = window.matchMedia("(min-width: 990px)");
    const isDesktop = () => mqDesktop.matches;
    // bottom sheet below 750px (af-cart-drawer.css); a side panel above it
    const mqSheet = window.matchMedia("(max-width: 749px)");

    const HIST_TOKEN = `af_cart_${Math.random().toString(36).slice(2)}`;

    /* -- Cached DOM references (avoids repeated querySelector) -- */
    let _cachedDrawer = null;
    let _cachedInner = null;
    let _cachedScroller = null;
    let _cachedOpenNodes = null;
    let drawerIsOpen = false; // cheap flag toggled by MutationObserver

    const getDrawer = () => {
      if (_cachedDrawer && _cachedDrawer.isConnected) return _cachedDrawer;
      _cachedDrawer = document.querySelector("cart-drawer");
      _cachedInner = null;
      _cachedScroller = null;
      _cachedOpenNodes = null;
      return _cachedDrawer;
    };

    const invalidateCache = () => {
      _cachedDrawer = null;
      _cachedInner = null;
      _cachedScroller = null;
      _cachedOpenNodes = null;
    };

    // Invalidate on Shopify section re-renders
    document.addEventListener("shopify:section:load", invalidateCache);

    const getOpenNodes = (d) => {
      if (_cachedOpenNodes && _cachedDrawer === d) return _cachedOpenNodes;
      _cachedOpenNodes = [
        d,
        d?.querySelector("#CartDrawer"),
        d?.querySelector(".cart-drawer"),
        d?.querySelector(".drawer"),
        d?.querySelector(".drawer__inner"),
      ].filter(Boolean);
      return _cachedOpenNodes;
    };

    const isOpen = (d) => {
      if (!d) return false;
      return getOpenNodes(d).some((n) =>
        n.classList.contains("active") ||
        n.hasAttribute("open") ||
        n.getAttribute("aria-hidden") === "false"
      );
    };

    function closeCartDrawer(fromBackGesture) {
      if (!drawerIsOpen) return;
      const d = getDrawer();
      if (!d) return;
      try {
        d.close?.();
      } catch (e) {}
    }

    const CART_HISTORY = {
      armed: false,
      pushed: false,

      _stateLooksOurs() {
        try {
          return !!(history.state && history.state.__af === HIST_TOKEN);
        } catch {
          return false;
        }
      },

      arm() {
        if (isDesktop()) return;

        // Already armed with our state => do not push again
        if (this._stateLooksOurs()) {
          this.armed = true;
          this.pushed = false;
          return;
        }

        try {
          // Push state WITHOUT URL change — prevents GA4/Meta false page views
          history.pushState({ __af: HIST_TOKEN }, "");
          this.armed = true;
          this.pushed = true;
        } catch (e) {
          this.armed = false;
          this.pushed = false;
        }
      },

      // Remove our history entry without navigating away.
      disarmSafely() {
        if (isDesktop()) return false;
        if (!this.armed) return false;
        if (!this.pushed || !this._stateLooksOurs()) {
          this.clearSessionFlags();
          return false;
        }

        try {
          history.back();
          return true;
        } catch (e) {
          this.clearSessionFlags();
          return false;
        }
      },

      // Called by popstate handler when user hits back
      onPopState() {
        if (isDesktop() || !drawerIsOpen) return;

        const d = getDrawer();
        if (!d) return;

        // Our state was popped — close the drawer
        if (!this._stateLooksOurs()) {
          closeCartDrawer(true);
          this.clearSessionFlags();
        }
      },

      clearSessionFlags() {
        this.armed = false;
        this.pushed = false;
      },
    };

    window.addEventListener("popstate", () => CART_HISTORY.onPopState());

    /* =========================================================
       Smooth effects: enable blur/shadow AFTER slide ends
       CSS uses .af-opened on cart-drawer (if you want)
       ========================================================= */
    const getInner = (d) => {
      if (_cachedInner && _cachedInner.isConnected) return _cachedInner;
      _cachedInner = d?.querySelector(".drawer__inner") || null;
      return _cachedInner;
    };
    const getScroller = (d) => {
      if (!(_cachedScroller && _cachedScroller.isConnected)) {
        _cachedScroller =
          d?.querySelector(".af-cd-scroll") ||
          d?.querySelector("cart-drawer-items") ||
          d?.querySelector(".drawer__contents") ||
          d?.querySelector("#CartDrawer") ||
          null;
      }
      // short screens: the whole panel scrolls, not the list (af-cart-drawer.css)
      if (_cachedScroller?.classList.contains("af-cd-scroll") && getComputedStyle(_cachedScroller).overflowY === "visible") {
        return getInner(d) || _cachedScroller;
      }
      return _cachedScroller;
    };

    // Track whether drag is actively closing (suppresses effects hooks)
    let dragClosing = false;

    const setupEffectsHooks = () => {
      const d = getDrawer();
      if (!d) return;

      const inner = getInner(d);
      if (!inner) return;

      const setOpened = (val) => d.classList.toggle("af-opened", !!val);

      inner.addEventListener(
        "transitionstart",
        (e) => {
          if (e.propertyName !== "transform" || dragClosing) return;
          setOpened(false);
        },
        { passive: true }
      );

      inner.addEventListener(
        "transitionend",
        (e) => {
          if (e.propertyName !== "transform" || dragClosing) return;
          setOpened(isOpen(d));
        },
        { passive: true }
      );
    };

    setupEffectsHooks();

    /* =========================================================
       History sync to open/close state (works for ANY open/close path)
       ========================================================= */
    let _syncRAF = 0;
    const syncHistoryToOpenState = () => {
      if (_syncRAF) cancelAnimationFrame(_syncRAF);
      _syncRAF = requestAnimationFrame(() => {
        _syncRAF = 0;

        const d = getDrawer();
        const openNow = !!(d && isOpen(d));
        drawerIsOpen = openNow;

        if (isDesktop()) {
          CART_HISTORY.clearSessionFlags();
          return;
        }

        if (openNow) {
          // Drawer opened -> arm history if not already armed
          if (!CART_HISTORY.armed) CART_HISTORY.arm();
          return;
        }

        // Drawer closed -> clear flags
        CART_HISTORY.clearSessionFlags();
      });
    };

    // Watch broadly (survives re-renders / class toggles anywhere inside drawer)
    const openObserver = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "attributes") {
          if (m.attributeName === "class" || m.attributeName === "open" || m.attributeName === "aria-hidden") {
            syncHistoryToOpenState();
            break;
          }
        }
      }
    });

    const observeOpenSignals = () => {
      const d = getDrawer();
      if (!d) return;

      try {
        // Observe drawer itself + known nodes that often get the open/active flags
        const nodes = getOpenNodes(d);
        nodes.forEach((n) => {
          try {
            openObserver.observe(n, { attributes: true, attributeFilter: ["class", "open", "aria-hidden"] });
          } catch {}
        });
      } catch {}

      syncHistoryToOpenState();
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", observeOpenSignals);
    } else {
      observeOpenSignals();
    }

    // Watch for cart-drawer injection. Scoped to document.body (not
    // documentElement) and auto-disconnects after 8 s to avoid prolonged
    // overhead during heavy page-load DOM activity.
    let _pendingRescan = 0;
    const drawerInjectObserver = new MutationObserver(() => {
      if (_pendingRescan) return;
      _pendingRescan = requestAnimationFrame(() => {
        _pendingRescan = 0;
        observeOpenSignals();
        if (getDrawer()) drawerInjectObserver.disconnect();
      });
    });
    if (getDrawer()) {
      observeOpenSignals();
    } else {
      const startObserving = () => {
        if (getDrawer()) { observeOpenSignals(); return; }
        drawerInjectObserver.observe(document.body, { childList: true, subtree: true });
        // Safety: auto-disconnect after 8 s — if drawer hasn't appeared by
        // then it likely never will (or appeared and we missed the mutation).
        setTimeout(() => {
          drawerInjectObserver.disconnect();
          // One final check in case it appeared after disconnect
          if (getDrawer()) observeOpenSignals();
        }, 8000);
      };
      if (document.body) {
        startObserving();
      } else {
        document.addEventListener("DOMContentLoaded", startObserving);
      }
    }

    /* =========================================================
       Optional: pre-disarm on close-button clicks (does NOT block theme logic)
       ========================================================= */
    const isProbablyCloseButton = (el) => {
      if (!el) return false;
      // "Close sizes" on a Recently viewed card only folds that card
      if (el.closest("[data-af-cd-rv]")) return false;
      return !!el.closest(
        [
          "#CartDrawer-Overlay",
          "button.drawer__close",
          ".drawer__close",
          "button[aria-label*='Close']",
          "button[aria-label*='close']",
          "button[name='close']",
          "[data-cart-drawer-close]",
          "[data-drawer-close]",
        ].join(",")
      );
    };

    document.addEventListener(
      "click",
      (e) => {
        if (!drawerIsOpen || isDesktop()) return;
        const d = getDrawer();
        if (!d) return;

        const t = e.target && e.target.nodeType === 3 ? e.target.parentElement : e.target;
        if (!(t instanceof Element)) return;

        // Only match close buttons INSIDE the cart drawer — not other overlays
        if (!d.contains(t)) return;
        if (!isProbablyCloseButton(t)) return;

        // Remove history entry without navigating away
        if (CART_HISTORY.armed) {
          CART_HISTORY.disarmSafely();
        }
      },
      true
    );

    // Esc closes the drawer from cart-drawer.js (keyup); drop our entry too
    document.addEventListener(
      "keydown",
      (e) => {
        if (e.key !== "Escape" || !drawerIsOpen || isDesktop()) return;
        const d = getDrawer();
        if (!d || !(e.target instanceof Node) || !d.contains(e.target)) return;
        if (CART_HISTORY.armed) CART_HISTORY.disarmSafely();
      },
      true
    );

    /* =========================================================
       Drag logic — pure inline transforms, scoped listeners

       Key performance decisions:
       1. NO permanent passive:false touchmove on document
          (that blocks compositor-thread scrolling for ALL touches)
       2. touchmove/end/cancel attached to window ONLY during
          a potential drag, detached immediately when not needed
       3. Direct inline transforms via setProperty('...','important')
       4. close() is called WITH inline styles still present —
          close() removes 'active' first, clears styles after via rAF
          (eliminates the one-frame snap-back-to-open stutter)
       ========================================================= */
    const isInteractive = (el) =>
      !!el.closest("a,button,input,textarea,select,summary,label,[role='button'],[data-no-drag],quantity-input,quantity-popover,cart-remove-button");

    const pointerY = (e) => {
      if (e.touches && e.touches.length) return e.touches[0].clientY;
      if (e.changedTouches && e.changedTouches.length) return e.changedTouches[0].clientY;
      return e.clientY;
    };
    const pointerX = (e) => {
      if (e.touches && e.touches.length) return e.touches[0].clientX;
      if (e.changedTouches && e.changedTouches.length) return e.changedTouches[0].clientX;
      return e.clientX;
    };

    // Drag state
    let isDown = false;
    let dragging = false;
    let decided = false;

    let startX = 0;
    let startY = 0;
    let curY = 0;

    // Velocity tracking (px/ms)
    let lastMoveY = 0;
    let lastMoveT = 0;
    let velocity = 0;

    let rafId = 0;
    let pendingDelta = 0;

    let activeDrawer = null;
    let activeInner = null;
    let startedFromHandleOrHeader = false;
    let isTouchSequence = false;

    const DRAG_THRESHOLD = 8;
    const CLOSE_FRACTION = 0.28;
    const FLICK_VELOCITY = 0.5;
    const EASE = "cubic-bezier(.2,.8,.2,1)";

    // Helpers: set/clear inline styles with !important to override CSS
    const setInlineTransform = (inner, val) => {
      inner.style.setProperty("transform", val, "important");
    };
    const setInlineTransition = (inner, val) => {
      inner.style.setProperty("transition", val, "important");
    };

    /* ---- Scoped listener management ----
       touchmove (passive:false) is ONLY attached when a drag is potentially
       starting, and detached as soon as we know it's not a drag or the
       drag ends. This keeps compositor-thread scrolling fast at all times. */
    let moveListenersAttached = false;

    const attachMoveListeners = (isTouch) => {
      if (moveListenersAttached) return;
      moveListenersAttached = true;
      if (isTouch) {
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onUp);
        window.addEventListener("touchcancel", onUp);
      } else {
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
      }
    };

    const detachMoveListeners = () => {
      if (!moveListenersAttached) return;
      moveListenersAttached = false;
      // Remove both touch and mouse — safe even if not attached
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onUp);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    const canArmDrag = (e) => {
      if (!drawerIsOpen || !mqSheet.matches) return null;
      const d = getDrawer();
      if (!d) return null;

      const inner = getInner(d);
      if (!inner) return null;

      const t = e.target;
      if (!t.closest(".drawer__inner")) return null;

      if (t.closest(".drawer__handle")) return { d, inner, fromTop: true };
      if (t.closest(".drawer__header") && !isInteractive(t)) return { d, inner, fromTop: true };

      if (isInteractive(t)) return null;

      const scroller = getScroller(d);
      if (scroller && scroller.scrollTop > 0) return null;

      return { d, inner, fromTop: false };
    };

    let cachedHeight = 600;

    const beginDrag = (inner) => {
      dragging = true;
      // Cache height ONCE at drag start — reading offsetHeight on every
      // touchmove forces synchronous layout reflow, which is very expensive
      // when the main thread is busy (e.g. home page lazy-loading sections)
      cachedHeight = inner.offsetHeight || 600;
      // Promote to compositor layer so transforms run on GPU even when
      // main thread is blocked by other scripts
      inner.style.willChange = "transform";
      // Kill CSS transition, we drive transform directly
      setInlineTransition(inner, "none");
      setInlineTransform(inner, "translate3d(0, 0, 0) translateZ(0)");
      curY = 0;
      velocity = 0;
    };

    const resetState = () => {
      isDown = false;
      dragging = false;
      decided = false;
      curY = 0;
      velocity = 0;
      activeDrawer = null;
      activeInner = null;
      startedFromHandleOrHeader = false;
      isTouchSequence = false;
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      detachMoveListeners();
    };

    const onDown = (e) => {
      if (e.type === "mousedown" && e.button !== 0) return;

      const armed = canArmDrag(e);
      if (!armed) return;

      activeDrawer = armed.d;
      activeInner = armed.inner;
      startedFromHandleOrHeader = armed.fromTop;
      isTouchSequence = e.type === "touchstart";

      isDown = true;
      dragging = false;
      decided = false;

      startX = pointerX(e);
      startY = pointerY(e);
      lastMoveY = startY;
      lastMoveT = performance.now();
      curY = 0;
      velocity = 0;

      // Attach move/end listeners now (scoped to this drag attempt)
      attachMoveListeners(isTouchSequence);
    };

    const onMove = (e) => {
      if (!isDown || !activeDrawer || !activeInner) return;

      const cx = pointerX(e);
      const cy = pointerY(e);
      const dx = cx - startX;
      const dy = cy - startY;

      if (!decided) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < DRAG_THRESHOLD) return;

        decided = true;

        const isVertical = Math.abs(dy) > Math.abs(dx) * 1.1;
        const isDownward = dy > 0;

        if (!isVertical || !isDownward) {
          // Not a drag — detach immediately so browser can scroll freely
          resetState();
          return;
        }

        if (!startedFromHandleOrHeader) {
          const scroller = getScroller(activeDrawer);
          if (scroller && scroller.scrollTop > 0) {
            resetState();
            return;
          }
        }

        beginDrag(activeInner);
      }

      if (!dragging) return;

      // Track velocity (exponential smoothing)
      const now = performance.now();
      const dt = Math.max(1, now - lastMoveT);
      velocity = 0.7 * velocity + 0.3 * ((cy - lastMoveY) / dt);
      lastMoveY = cy;
      lastMoveT = now;

      // Downward only, rubber-band past 50%
      const rawDy = Math.max(0, dy);
      const h = cachedHeight;
      const halfH = h * 0.5;
      const delta = rawDy <= halfH
        ? rawDy
        : halfH + (rawDy - halfH) * 0.4;
      curY = delta;

      // Batch DOM write with rAF — direct inline transform
      pendingDelta = delta;
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          if (activeInner) {
            setInlineTransform(activeInner, `translate3d(0, ${pendingDelta}px, 0) translateZ(0)`);
          }
        });
      }

      if (e.cancelable) e.preventDefault();
    };

    const onUp = () => {
      if (!isDown || !activeDrawer) return;

      if (!dragging) {
        resetState();
        return;
      }

      const d = activeDrawer;
      const inner = activeInner;
      const h = cachedHeight;
      const savedCurY = curY;

      const closeByDistance = savedCurY > h * CLOSE_FRACTION;
      const closeByFlick = velocity > FLICK_VELOCITY;
      const shouldClose = closeByDistance || closeByFlick;

      // Flush any pending rAF write
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      detachMoveListeners();

      if (shouldClose && inner) {
        // Suppress effects hooks during drag-close animation
        dragClosing = true;

        // Slide off-screen: set transition, force reflow, set final position
        setInlineTransition(inner, `transform 200ms ${EASE}`);
        inner.offsetHeight; // eslint-disable-line no-unused-expressions
        setInlineTransform(inner, `translate3d(0, ${h + 40}px, 0) translateZ(0)`);

        const cleanup = () => {
          dragClosing = false;
          // Guard: if element was removed from DOM during animation, bail
          if (!d?.isConnected) return;
          d.classList.remove("af-opened");

          // Disarm history entry, then close drawer
          // close() removes 'active' FIRST (while inline transform keeps drawer off-screen),
          // then clears inline styles via rAF — no visual snap-back
          if (!isDesktop() && CART_HISTORY.armed) {
            CART_HISTORY.disarmSafely();
          }
          d.close?.();
        };

        let done = false;
        const finish = () => { if (done) return; done = true; cleanup(); };
        inner.addEventListener("transitionend", (ev) => {
          if (ev.propertyName === "transform") finish();
        }, { once: true });
        setTimeout(finish, 300); // fallback if transitionend doesn't fire
      } else if (inner) {
        // Snap back: transition from current drag position to 0
        setInlineTransition(inner, `transform 260ms ${EASE}`);
        inner.offsetHeight; // eslint-disable-line no-unused-expressions
        setInlineTransform(inner, "translate3d(0, 0, 0) translateZ(0)");

        let done = false;
        const finish = () => {
          if (done) return; done = true;
          inner.style.removeProperty("transform");
          inner.style.removeProperty("transition");
          inner.style.removeProperty("will-change");
        };
        inner.addEventListener("transitionend", (ev) => {
          if (ev.propertyName === "transform") finish();
        }, { once: true });
        setTimeout(finish, 350);
      }

      // Reset drag state (listeners already detached above)
      isDown = false;
      dragging = false;
      decided = false;
      curY = 0;
      velocity = 0;
      activeDrawer = null;
      activeInner = null;
      startedFromHandleOrHeader = false;
      isTouchSequence = false;
    };

    // ONLY touchstart/mousedown are permanent (passive, lightweight).
    // touchmove/end/cancel are scoped — attached in onDown, detached in resetState/onUp.
    document.addEventListener("touchstart", onDown, { passive: true, capture: true });
    document.addEventListener("mousedown", onDown, { capture: true });

  } catch (err) {
    console.error("[AF cart drag SAFE] failed:", err);
  }
})();

/* =========================================================
   AF — Offer nudge on the /cart page (sections/main-cart-footer.liquid)
   Fills [data-af-cart-nudge] ("Add ৳2,186 more for 10% off") from
   assets/af-promos.json (same data + rules as af-offers.js).
   The element is server-rendered with the cart subtotal and comes back
   with every cart re-render, so we just fill any new one. The drawer uses
   the offers bar instead (snippets/af-offers.liquid). Store Credit mode on:
   main-cart-footer prints no element, so nothing here runs.
   ========================================================= */
(() => {
  if (window.__afCartNudge) return;
  window.__afCartNudge = true;

  // CART-02: cache promos across page loads (sessionStorage, short TTL) so a
  // later drawer open — even on a fresh page — renders the nudge at once
  // instead of popping in 100-300ms after the drawer is already open.
  const PROMOS_CACHE_KEY = 'af_cart_promos_v1';
  const PROMOS_TTL = 5 * 60 * 1000; // 5 minutes — matches af-offers.js

  const readPromosCache = (url) => {
    try {
      const raw = sessionStorage.getItem(PROMOS_CACHE_KEY);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (!cached || cached.url !== url || !Array.isArray(cached.promos)) return null;
      if (Date.now() - cached.time > PROMOS_TTL) return null;
      return cached.promos;
    } catch {
      return null;
    }
  };

  const writePromosCache = (url, promos) => {
    try {
      sessionStorage.setItem(PROMOS_CACHE_KEY, JSON.stringify({ url, time: Date.now(), promos }));
    } catch {}
  };

  let promosP = null;
  const getPromos = (url) => {
    if (promosP) return promosP;
    const cached = readPromosCache(url);
    if (cached) return (promosP = Promise.resolve(cached));
    return (promosP = fetch(url, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => (d && Array.isArray(d.promos) ? d.promos : []))
      .then((promos) => { writePromosCache(url, promos); return promos; })
      .catch(() => { promosP = null; return []; }));
  };

  const fmt = (n) => '<span class="af-tk">৳</span>' + new Intl.NumberFormat('en-US').format(Math.ceil(n));
  const isActive = (p, now) =>
    p && p.active !== false && p.discount &&
    !(p.starts_at && now < p.starts_at) && !(p.ends_at && now > p.ends_at);
  const offerLabel = (p) =>
    p.discount.kind === 'percent' ? `${p.discount.value}% off` : `${fmt(p.discount.value)} off`;

  async function fill(el) {
    const key = el.dataset.subtotal || '0';
    if (el.dataset.afDone === key) return;
    el.dataset.afDone = key;
    const sub = Number(key) / 100;

    // CART-02: getPromos() resolves near-instantly from cache/memory — only
    // a genuine first-ever network fetch takes long enough to be seen. Fade
    // the line in for that case instead of letting it pop in and shove the
    // Checkout button down.
    const t0 = performance.now();
    const promos = await getPromos(el.dataset.promosUrl);
    const wasSlow = performance.now() - t0 > 50;

    const now = new Date().toISOString();
    const active = promos.filter((p) => isActive(p, now) && Number(p.min_subtotal || 0) > 0);
    const next = active
      .filter((p) => Number(p.min_subtotal) > sub)
      .sort((a, b) => a.min_subtotal - b.min_subtotal)[0];
    const got = active
      .filter((p) => Number(p.min_subtotal) <= sub)
      .sort((a, b) => b.min_subtotal - a.min_subtotal)[0];

    if (sub <= 0 || (!next && !got)) { el.hidden = true; return; }
    const target = next || got;
    const pct = Math.min(100, (sub / Number(target.min_subtotal)) * 100);

    // N2: keep the same bar/text nodes across re-fills instead of wiping
    // them out with el.innerHTML each time — replacing the bar element on
    // every render meant its CSS transition never had a previous frame to
    // animate from.
    let textEl = el.querySelector('.af-cart-nudge__text');
    if (!textEl) {
      textEl = document.createElement('span');
      textEl.className = 'af-cart-nudge__text';
      el.appendChild(textEl);
    }
    textEl.innerHTML = next
      ? `Add <b>${fmt(next.min_subtotal - sub)}</b> more for <b>${offerLabel(next)}</b>`
      : `<b>${offerLabel(got)}</b> unlocked, applied at checkout`;

    let bar = el.querySelector('.af-cart-nudge__bar');
    let barFill = bar && bar.querySelector('i');
    if (!bar) {
      bar = document.createElement('span');
      bar.className = 'af-cart-nudge__bar';
      bar.setAttribute('aria-hidden', 'true');
      barFill = document.createElement('i');
      bar.appendChild(barFill);
      el.appendChild(bar);
    }
    // SYS-05 / Motion #6: fill via transform: scaleX (compositor-only) instead
    // of animating width. Same look at rest — the bar element is full width,
    // the fill is scaled from its left edge.
    barFill.style.transform = `scaleX(${(pct / 100).toFixed(4)})`;
    el.classList.toggle('is-unlocked', !next);
    el.hidden = false;

    if (wasSlow) {
      el.classList.add('af-cart-nudge--enter');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => el.classList.remove('af-cart-nudge--enter'));
      });
    }
  }

  let queued = false;
  const scan = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      document.querySelectorAll('[data-af-cart-nudge]').forEach(fill);
    });
  };

  const start = () => {
    scan();
    document.querySelectorAll('#main-cart-footer').forEach((root) =>
      new MutationObserver(scan).observe(root, { childList: true, subtree: true })
    );
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* =========================================================
   AF — Cart drawer redesign (Sep 2026). Markup: snippets/cart-drawer.liquid
   Dawn re-draws the drawer on every cart change, so each piece here is
   re-applied after a re-draw (a MutationObserver on <cart-drawer>):
   - "Added · Hawas Ice · 5ML" banner for 4s after an add (product page,
     sticky bar, quick add, the strip below) and the new line tinted copper
     for 2.4s, scrolled into view. Never on quantity changes.
   - the order note keeps the shopper's open/closed choice
   - the offers bar fill slides from where it was (Store Credit mode off)
   - the list keeps its scroll position across quantity changes
   - Recently viewed strip: products from the Recently viewed history
     (localStorage, assets/af-recently-viewed.js) minus what's in the cart
     and what's sold out; "+" opens the sizes, a size goes straight into
     the cart. Product data is fetched on the first open and cached for
     the session.
   ========================================================= */
(() => {
  if (window.__afCartUi) return;
  window.__afCartUi = true;

  const getDrawer = () => document.querySelector("cart-drawer.af-cd");
  const reduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const fmt = (cents) => "৳" + Math.round(Number(cents) / 100).toLocaleString("en-US");
  // "5ML Decant (75+ sprays)" -> "5ML"; one-size products have no size
  const sizeLabel = (title) => {
    const t = String(title || "").trim();
    if (!t || /^default title$/i.test(t)) return "";
    return t.split("(")[0].replace(/\s+decant\b/gi, "").trim();
  };
  const readJSON = (store, key) => {
    try {
      return JSON.parse(store.getItem(key) || "null");
    } catch (e) {
      return null;
    }
  };

  /* ---------- what was just added ---------- */
  const submitted = new Map(); // variant id -> quantity, from the add form just submitted
  document.addEventListener(
    "submit",
    (e) => {
      const f = e.target;
      if (!(f instanceof HTMLFormElement) || !/\/cart\/add/.test(f.getAttribute("action") || "")) return;
      try {
        const fd = new FormData(f); // includes inputs linked by form= (the product page quantity)
        const id = String(fd.get("id") || "");
        if (id) submitted.set(id, Math.max(1, parseInt(fd.get("quantity"), 10) || 1));
      } catch (err) {}
    },
    true
  );

  let added = null; // { key, text, at, shownAt, scrolled }
  let bannerTimer = 0;

  function onCartUpdate(ev) {
    if (!ev || (ev.source !== "product-form" && ev.source !== "af-cart-drawer")) return;
    const item = ev.cartData;
    if (!item || !item.key || item.status) return;
    const vid = String(ev.productVariantId || item.variant_id || "");
    const qty = submitted.get(vid) || 1;
    submitted.delete(vid);
    const text = [item.product_title, sizeLabel(item.variant_title)].filter(Boolean).join(" · ");
    added = { key: item.key, text: text + (qty > 1 ? " × " + qty : ""), at: Date.now(), shownAt: 0, scrolled: false };
    // the banner and tint go on the re-drawn drawer, not the one on screen now
    const d = getDrawer();
    if (d) d.querySelectorAll("[data-af-cd-added], .af-cd-item").forEach((n) => { n.__afOld = true; });
  }
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }

  const findRow = (d, key) =>
    [...d.querySelectorAll(".af-cd-item[data-line-key]")].find((r) => !r.__afOld && r.dataset.lineKey === key);

  // the list scrolls; on short screens the whole panel does (af-cart-drawer.css)
  function scrollerOf(d) {
    const sc = d.querySelector(".af-cd-scroll");
    if (sc && getComputedStyle(sc).overflowY === "visible") return d.querySelector(".drawer__inner");
    return sc;
  }

  function reveal(sc, row) {
    if (!sc || !row) return;
    const r = row.getBoundingClientRect();
    const s = sc.getBoundingClientRect();
    // when the panel scrolls, the pinned footer covers its bottom
    const foot = sc.classList.contains("drawer__inner") ? sc.querySelector(".af-cd-foot") : null;
    const bottom = s.bottom - (foot ? foot.offsetHeight : 0);
    let by = 0;
    if (r.top < s.top) by = r.top - s.top - 8;
    else if (r.bottom > bottom) by = r.bottom - bottom + 8;
    if (by) sc.scrollTo({ top: sc.scrollTop + by, behavior: reduced() ? "auto" : "smooth" });
  }

  function applyAdded(d) {
    if (!added) return;
    const now = Date.now();
    if (!added.shownAt) {
      if (now - added.at > 10000) { added = null; return; } // the re-draw never came
      if (!findRow(d, added.key)) return; // not re-drawn yet (quick add closes first)
      added.shownAt = now;
    }
    const age = now - added.shownAt;
    if (age >= 4000) { added = null; return; }

    const banner = d.querySelector("[data-af-cd-added]");
    if (banner && !banner.__afOld && !banner.__afCd) {
      banner.__afCd = true;
      const txt = banner.querySelector("[data-af-cd-added-text]");
      if (txt) txt.textContent = added.text;
      if (age < 60 && !reduced()) {
        requestAnimationFrame(() => requestAnimationFrame(() => banner.classList.add("is-open")));
      } else {
        banner.classList.add("is-instant", "is-open"); // re-drawn while showing: no second slide
      }
      clearTimeout(bannerTimer);
      bannerTimer = setTimeout(() => {
        const dd = getDrawer();
        const b = dd && dd.querySelector("[data-af-cd-added]");
        if (b) b.classList.remove("is-instant", "is-open");
        added = null;
      }, 4000 - age);
    }

    const row = findRow(d, added.key);
    if (row && !row.__afCd && age < 2400) {
      row.__afCd = true;
      row.classList.add("is-added");
      setTimeout(() => row.classList.add("is-fading"), 2400 - age);
      if (!added.scrolled) {
        added.scrolled = true;
        requestAnimationFrame(() => reveal(scrollerOf(d), row));
      }
    }
  }

  /* ---------- list scroll position ---------- */
  let scrollTop = 0;
  function applyScroll(d) {
    const sc = scrollerOf(d);
    if (!sc || sc.__afCd) return;
    sc.__afCd = true;
    if (scrollTop) sc.scrollTop = scrollTop; // a quantity change re-draws the list: stay put
    sc.addEventListener("scroll", () => { scrollTop = sc.scrollTop; }, { passive: true });
  }

  /* ---------- order note ---------- */
  let noteOpen = null; // null = as drawn (open when the cart has a note)
  function setNote(box, open) {
    box.classList.toggle("is-open", open);
    const t = box.querySelector(".af-cd-note__toggle");
    if (t) t.setAttribute("aria-expanded", open ? "true" : "false");
  }
  function applyNote(d) {
    const box = d.querySelector("[data-af-cd-note]");
    if (!box || box.__afCd) return;
    box.__afCd = true;
    if (noteOpen === null || box.classList.contains("is-open") === noteOpen) return;
    box.classList.add("is-instant");
    setNote(box, noteOpen);
    requestAnimationFrame(() => requestAnimationFrame(() => box.classList.remove("is-instant")));
  }

  /* ---------- offers bar ---------- */
  let lastFill = null; // null: first paint, don't slide
  function applyOffers(d) {
    const wrap = d.querySelector("[data-af-cd-offers]");
    if (!wrap) {
      if (d.classList.contains("is-empty")) lastFill = "0%"; // the first add slides from empty
      return;
    }
    if (wrap.__afCd) return;
    wrap.__afCd = true;
    const bar = wrap.querySelector(".af-offers");
    const fill = bar && bar.querySelector(".af-offers__fill");
    if (!fill) return;
    const target = fill.style.width;
    if (lastFill !== null && lastFill !== target && !reduced()) {
      fill.style.transition = "none";
      fill.style.width = lastFill;
      void fill.offsetWidth;
      fill.style.transition = "";
      fill.style.width = target;
    }
    lastFill = target;
    // af-promos.json may say something the Liquid copy doesn't; the bar redraws itself then
    if (window.AFOffers && typeof window.AFOffers.render === "function") {
      Promise.resolve(window.AFOffers.render(bar, Number(wrap.dataset.subtotal) || 0)).then(() => {
        if (fill.isConnected) lastFill = fill.style.width;
      });
    }
  }

  /* ---------- Recently viewed strip ---------- */
  const RV_KEY = "af_recently_viewed_handles"; // written by af-recently-viewed.js
  const RV_CACHE = "af_cd_rv_v1";
  const RV_TTL = 10 * 60 * 1000;
  const RV_MAX = 8;
  let rvList = null;
  let rvPromise = null;
  let rvShown = false;

  const numOf = (t) => {
    const m = String(t || "").match(/^\s*(\d+)/);
    return m ? parseInt(m[1], 10) : 99999;
  };
  function slim(p) {
    return {
      id: p.id,
      title: p.title,
      url: p.url || "/products/" + p.handle,
      img: p.featured_image || (p.images && p.images[0]) || "",
      available: !!p.available,
      variants: (p.variants || [])
        .map((v) => ({ id: v.id, title: v.title, option1: v.option1, price: v.price, available: !!v.available }))
        .sort((a, b) => numOf(a.option1 || a.title) - numOf(b.option1 || b.title)),
    };
  }
  function loadStrip() {
    if (rvPromise) return rvPromise;
    let handles = readJSON(localStorage, RV_KEY);
    handles = (Array.isArray(handles) ? handles : []).filter((h) => typeof h === "string" && h).slice(0, 12);
    const cache = readJSON(sessionStorage, RV_CACHE) || {};
    const now = Date.now();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    rvPromise = Promise.all(
      handles.map((h) => {
        const hit = cache[h];
        if (hit && hit.p && now - hit.at < RV_TTL) return Promise.resolve(hit.p);
        return fetch(root + "products/" + encodeURIComponent(h) + ".js", { credentials: "same-origin" })
          .then((r) => (r.ok ? r.json() : null))
          .then((p) => {
            if (!p || !p.id) return null;
            const s = slim(p);
            cache[h] = { at: Date.now(), p: s };
            return s;
          })
          .catch(() => null);
      })
    ).then((list) => {
      try { sessionStorage.setItem(RV_CACHE, JSON.stringify(cache)); } catch (e) {}
      rvList = list.filter(Boolean);
      return rvList;
    });
    return rvPromise;
  }

  const SVG = (paths) =>
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    paths +
    "</svg>";
  const ICON_PLUS = SVG('<path d="M12 5v14M5 12h14"/>');
  const ICON_CLOSE = SVG('<path d="M6 6l12 12M18 6L6 18"/>');
  const sized = (url, w) => (url ? url + (url.includes("?") ? "&" : "?") + "width=" + w : "");
  const chipLabel = (v) => String(v.option1 || v.title || "").split(" ")[0].toUpperCase();

  function make(tag, cls, attrs) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) Object.keys(attrs).forEach((k) => n.setAttribute(k, attrs[k]));
    return n;
  }

  function buildCard(p) {
    const card = make("div", "af-cd-rv__card", { role: "listitem" });
    card.dataset.productId = String(p.id);
    const single = p.variants.length <= 1;
    const avail = p.variants.filter((v) => v.available);
    const min = Math.min(...avail.map((v) => v.price));

    const thumb = make("a", "af-cd-rv__thumb", { href: p.url, tabindex: "-1", "aria-hidden": "true" });
    if (p.img) {
      thumb.appendChild(
        make("img", "", { src: sized(p.img, 80), alt: "", width: "40", height: "40", loading: "lazy", decoding: "async" })
      );
    }
    const info = make("div", "af-cd-rv__info");
    const title = make("a", "af-cd-rv__title", { href: p.url });
    title.textContent = p.title;
    const from = make("span", "af-cd-rv__from");
    from.textContent = (avail.length > 1 ? "From " : "") + fmt(min);
    info.append(title, from);

    const add = make("button", "af-cd-rv__add", {
      type: "button",
      "aria-label": single ? "Add " + p.title + " to cart, " + fmt(min) : "Choose a size of " + p.title,
    });
    if (single) add.dataset.variantId = String(avail[0].id);
    else add.setAttribute("aria-expanded", "false");
    add.innerHTML = ICON_PLUS;
    card.append(thumb, info, add);

    if (!single) {
      const sizes = make("div", "af-cd-rv__sizes", { role: "group", "aria-label": "Sizes of " + p.title });
      sizes.hidden = true;
      p.variants.forEach((v) => {
        const ml = chipLabel(v);
        const b = make("button", "af-cd-rv__size", {
          type: "button",
          "aria-label": v.available ? "Add " + p.title + " " + ml + ", " + fmt(v.price) : ml + ", sold out",
        });
        b.dataset.variantId = String(v.id);
        if (!v.available) b.disabled = true;
        const a = make("span", "af-cd-rv__size-ml");
        a.textContent = ml;
        const c = make("span", "af-cd-rv__size-p");
        c.textContent = v.available ? fmt(v.price) : "Sold out";
        b.append(a, c);
        sizes.appendChild(b);
      });
      const close = make("button", "af-cd-rv__close", { type: "button", "aria-label": "Close sizes" });
      close.hidden = true;
      close.innerHTML = ICON_CLOSE;
      card.append(sizes, close);
    }
    return card;
  }

  function syncArrows(box) {
    const track = box.querySelector("[data-af-cd-rv-track]");
    if (!track || !track.clientWidth) return;
    const max = track.scrollWidth - track.clientWidth;
    box.classList.toggle("is-static", max <= 2);
    box.querySelectorAll("[data-af-cd-rv-step]").forEach((b) => {
      b.disabled = b.dataset.afCdRvStep === "-1" ? track.scrollLeft <= 2 : track.scrollLeft >= max - 2;
    });
  }

  function applyStrip(d) {
    const box = d.querySelector("[data-af-cd-rv]");
    if (!box) return;
    if (box.__afCd) { syncArrows(box); return; }
    if (!rvList) {
      if (d.classList.contains("active")) {
        loadStrip().then(() => {
          const dd = getDrawer();
          if (dd) applyStrip(dd);
        });
      }
      return;
    }
    box.__afCd = true;
    const inCart = new Set(String(box.dataset.cartProducts || "").split(",").filter(Boolean));
    const list = rvList.filter((p) => p.available && !inCart.has(String(p.id))).slice(0, RV_MAX);
    const track = box.querySelector("[data-af-cd-rv-track]");
    if (!track || !list.length) { box.hidden = true; return; }
    track.textContent = "";
    list.forEach((p) => track.appendChild(buildCard(p)));
    track.addEventListener("scroll", () => syncArrows(box), { passive: true });
    // a vertical mouse wheel scrolls the strip sideways while it can move
    track.addEventListener(
      "wheel",
      (e) => {
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        const max = track.scrollWidth - track.clientWidth;
        if (max <= 0) return;
        if ((e.deltaY < 0 && track.scrollLeft <= 0) || (e.deltaY > 0 && track.scrollLeft >= max - 1)) return;
        track.scrollLeft = Math.max(0, Math.min(max, track.scrollLeft + e.deltaY));
        e.preventDefault();
      },
      { passive: false }
    );
    const first = !rvShown;
    rvShown = true;
    if (first && !reduced()) {
      box.classList.add("is-entering");
      box.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => box.classList.remove("is-entering")));
    } else {
      box.hidden = false;
    }
    syncArrows(box);
  }

  function openCard(card) {
    const track = card.parentElement;
    track.querySelectorAll(".af-cd-rv__card.is-open").forEach((c) => { if (c !== card) closeCard(c, false); });
    card.classList.add("is-open");
    card.querySelector(".af-cd-rv__info").hidden = true;
    const add = card.querySelector(".af-cd-rv__add");
    add.hidden = true;
    add.setAttribute("aria-expanded", "true");
    card.querySelector(".af-cd-rv__sizes").hidden = false;
    card.querySelector(".af-cd-rv__close").hidden = false;
    const firstSize = card.querySelector(".af-cd-rv__size:not(:disabled)");
    if (firstSize) firstSize.focus({ preventScroll: true });
    // the card grows to 312px: keep it in view
    setTimeout(() => {
      const r = card.getBoundingClientRect();
      const t = track.getBoundingClientRect();
      let by = 0;
      if (r.right > t.right - 12) by = r.right - t.right + 20;
      else if (r.left < t.left) by = r.left - t.left - 20;
      if (by) track.scrollBy({ left: by, behavior: reduced() ? "auto" : "smooth" });
      const box = card.closest("[data-af-cd-rv]");
      if (box) syncArrows(box);
    }, reduced() ? 0 : 320);
  }
  function closeCard(card, focusBack) {
    card.classList.remove("is-open", "is-busy");
    card.querySelector(".af-cd-rv__info").hidden = false;
    const add = card.querySelector(".af-cd-rv__add");
    add.hidden = false;
    add.setAttribute("aria-expanded", "false");
    card.querySelector(".af-cd-rv__sizes").hidden = true;
    card.querySelector(".af-cd-rv__close").hidden = true;
    if (focusBack) add.focus({ preventScroll: true });
  }

  async function addVariant(card, id, btn) {
    const d = getDrawer();
    if (!d || card.classList.contains("is-busy")) return;
    const box = card.closest("[data-af-cd-rv]");
    const err = box && box.querySelector("[data-af-cd-rv-err]");
    if (err) { err.hidden = true; err.textContent = ""; }
    card.classList.add("is-busy");
    btn.setAttribute("aria-busy", "true");

    const fd = new FormData();
    fd.append("id", id);
    fd.append("quantity", "1");
    const sections =
      typeof d.getSectionsToRender === "function"
        ? d.getSectionsToRender().map((s) => s.id)
        : ["cart-drawer", "cart-icon-bubble"];
    fd.append("sections", sections.join(","));
    fd.append("sections_url", window.location.pathname);
    const config =
      typeof fetchConfig === "function"
        ? fetchConfig("javascript")
        : { method: "POST", headers: { Accept: "application/javascript" } };
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];
    config.body = fd;

    try {
      const res = await fetch((window.routes && window.routes.cart_add_url) || "/cart/add", config);
      // A 429/503 page is HTML, not JSON: show the friendly message, not a parse error.
      const data = await res.json().catch(() => null);
      if (!data || !res.ok || data.status) {
        throw new Error((data && (data.description || data.message)) || "Couldn’t add that. Please try again.");
      }
      submitted.set(String(id), 1);
      const ev = { source: "af-cart-drawer", productVariantId: String(id), cartData: data };
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") publish(PUB_SUB_EVENTS.cartUpdate, ev);
      else onCartUpdate(ev);
      d.renderContents(data); // re-draws the drawer: the product leaves the strip, the line appears
    } catch (e) {
      card.classList.remove("is-busy");
      btn.removeAttribute("aria-busy");
      if (err) { err.textContent = e.message; err.hidden = false; }
    }
  }

  /* ---------- clicks + keys ---------- */
  document.addEventListener("click", (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t || !t.closest("cart-drawer.af-cd")) return;

    const toggle = t.closest(".af-cd-note__toggle");
    if (toggle) {
      const box = toggle.closest("[data-af-cd-note]");
      noteOpen = !box.classList.contains("is-open");
      setNote(box, noteOpen);
      if (noteOpen) {
        const ta = box.querySelector("textarea");
        if (ta) {
          // the note is the last thing in the list: once it has finished
          // opening, bring the whole box into view, then focus the field
          const grow = box.querySelector(".af-cd-note__box");
          let done = false;
          const onEnd = (ev) => { if (ev.target === grow) go(); };
          const go = () => {
            if (done) return;
            done = true;
            if (grow) grow.removeEventListener("transitionend", onEnd);
            reveal(scrollerOf(box.closest("cart-drawer")), box);
            ta.focus({ preventScroll: true });
          };
          if (reduced() || !grow) go();
          else {
            grow.addEventListener("transitionend", onEnd);
            setTimeout(go, 450);
          }
        }
      }
      return;
    }

    const step = t.closest("[data-af-cd-rv-step]");
    if (step) {
      const track = step.closest("[data-af-cd-rv]").querySelector("[data-af-cd-rv-track]");
      track.scrollBy({ left: Number(step.dataset.afCdRvStep) * 244, behavior: reduced() ? "auto" : "smooth" });
      return;
    }

    const card = t.closest(".af-cd-rv__card");
    if (!card) return;
    const add = t.closest(".af-cd-rv__add");
    if (add) {
      if (add.dataset.variantId) addVariant(card, add.dataset.variantId, add);
      else openCard(card);
      return;
    }
    if (t.closest(".af-cd-rv__close")) { closeCard(card, true); return; }
    const size = t.closest(".af-cd-rv__size");
    if (size && !size.disabled) addVariant(card, size.dataset.variantId, size);
  });

  // Escape inside an open card folds it; the drawer stays open
  document.addEventListener(
    "keyup",
    (e) => {
      if (e.key !== "Escape") return;
      const a = document.activeElement;
      const card = a && a.closest && a.closest("cart-drawer .af-cd-rv__card.is-open");
      if (!card) return;
      e.stopPropagation();
      closeCard(card, true);
    },
    true
  );

  /* ---------- re-apply after every re-draw ---------- */
  function applyAll() {
    const d = getDrawer();
    if (!d) return;
    applyScroll(d);
    applyNote(d);
    applyOffers(d);
    applyAdded(d);
    applyStrip(d);
    // Store Credit block (assets/af-cashback-cart.js, only loaded when its mode is on): re-clamp
    // the slider to the re-drawn subtotal. Its failure must never break the drawer.
    if (window.AFCashback && window.AFCashback.cart) {
      try { window.AFCashback.cart.apply(d); } catch (e) {}
    }
  }
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      applyAll();
    });
  };
  function start() {
    const d = getDrawer();
    if (!d) return;
    new MutationObserver(schedule).observe(d, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    applyAll();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();

/* =========================================================
   AF — open the drawer after /cart sent the shopper back here
   (layout/theme.liquid: checkout's "cart" link lands on /cart, which
   returns to the page they were on and sets this flag).
   ========================================================= */
(() => {
  try {
    if (sessionStorage.getItem("af-open-cart") !== "1") return;
    sessionStorage.removeItem("af-open-cart");
  } catch (e) {
    return;
  }
  const open = () => {
    const d = document.querySelector("cart-drawer");
    if (d && typeof d.open === "function") d.open();
  };
  customElements.whenDefined("cart-drawer").then(() => {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", open, { once: true });
    else open();
  });
})();
